/**
 * Typing Sound Effect Generator
 * ==============================
 * Plays a realistic keyboard typing sound during function calls (RAG search,
 * Calendly slot fetch, etc.) to fill latency pauses and make the bot feel
 * like a real agent looking things up.
 *
 * The sound is generated entirely with Web Audio API — no external audio files.
 * Each "keystroke" is a short filtered noise burst with randomized timing,
 * pitch, and volume for a natural feel.
 *
 * Usage:
 *   import { typingSound } from '../utils/typing-sound'
 *
 *   // Start typing when function call begins
 *   typingSound.start()
 *
 *   // Stop when function call completes
 *   typingSound.stop()
 */

// Sound profiles for different operation types
export type SoundProfile = 'typing' | 'clicking'

interface TypingSoundConfig {
  /** Base volume (0-1). Kept low so it doesn't overpower the voice */
  volume: number
  /** Base interval between keystrokes in ms */
  baseInterval: number
  /** Random jitter range added to interval (±ms) */
  intervalJitter: number
  /** Keystroke duration in seconds */
  clickDuration: number
  /** Base frequency for the click filter (Hz) */
  baseFrequency: number
  /** Frequency jitter range (±Hz) */
  frequencyJitter: number
  /** Fade-out duration in ms */
  fadeOutDuration: number
}

const PROFILES: Record<SoundProfile, TypingSoundConfig> = {
  typing: {
    volume: 0.22,
    baseInterval: 80,
    intervalJitter: 60,
    clickDuration: 0.025,
    baseFrequency: 1800,
    frequencyJitter: 600,
    fadeOutDuration: 250,
  },
  clicking: {
    volume: 0.18,
    baseInterval: 300,
    intervalJitter: 150,
    clickDuration: 0.015,
    baseFrequency: 2400,
    frequencyJitter: 400,
    fadeOutDuration: 200,
  },
}

class TypingSoundGenerator {
  private audioCtx: AudioContext | null = null
  private gainNode: GainNode | null = null
  private isPlaying = false
  private timeoutId: ReturnType<typeof setTimeout> | null = null
  private currentProfile: SoundProfile = 'typing'

  /**
   * Get or create the AudioContext (lazy init to comply with browser autoplay).
   * Reuses existing context if available.
   */
  private getAudioContext(): AudioContext {
    if (!this.audioCtx || this.audioCtx.state === 'closed') {
      this.audioCtx = new AudioContext()
    }
    // Resume if suspended (browser autoplay policy)
    if (this.audioCtx.state === 'suspended') {
      this.audioCtx.resume()
    }
    return this.audioCtx
  }

  /**
   * Play a single keystroke click.
   * Uses a short burst of filtered white noise to simulate a mechanical key press.
   */
  private playClick(): void {
    const ctx = this.getAudioContext()
    const config = PROFILES[this.currentProfile]

    // Create a short buffer of white noise
    const duration = config.clickDuration
    const sampleRate = ctx.sampleRate
    const bufferSize = Math.floor(duration * sampleRate)
    const buffer = ctx.createBuffer(1, bufferSize, sampleRate)
    const data = buffer.getChannelData(0)

    // Fill with white noise (random samples)
    for (let i = 0; i < bufferSize; i++) {
      data[i] = (Math.random() * 2 - 1)
    }

    // Apply a quick amplitude envelope (attack + decay) for a snappy click
    const attackSamples = Math.floor(0.001 * sampleRate) // 1ms attack
    const decaySamples = bufferSize - attackSamples
    for (let i = 0; i < attackSamples; i++) {
      data[i] *= i / attackSamples
    }
    for (let i = 0; i < decaySamples; i++) {
      data[attackSamples + i] *= 1 - (i / decaySamples)
    }

    // Create source
    const source = ctx.createBufferSource()
    source.buffer = buffer

    // Bandpass filter to shape the noise into a key-click tone
    const filter = ctx.createBiquadFilter()
    filter.type = 'bandpass'
    filter.frequency.value = config.baseFrequency + (Math.random() * 2 - 1) * config.frequencyJitter
    filter.Q.value = 1.5 + Math.random() * 1.5

    // Gain for this individual click (slight random variation)
    const clickGain = ctx.createGain()
    const volumeVariation = 0.6 + Math.random() * 0.4 // 60-100% of base volume
    clickGain.gain.value = volumeVariation

    // Connect: source → filter → clickGain → masterGain → destination
    source.connect(filter)
    filter.connect(clickGain)
    clickGain.connect(this.gainNode!)

    source.start()
    source.stop(ctx.currentTime + duration)
  }

  /**
   * Schedule the next keystroke with randomized timing.
   * Occasionally inserts a longer pause to simulate word boundaries.
   */
  private scheduleNext(): void {
    if (!this.isPlaying) return

    const config = PROFILES[this.currentProfile]
    let delay = config.baseInterval + (Math.random() * 2 - 1) * config.intervalJitter

    // ~15% chance of a longer pause (word boundary / thinking pause)
    if (Math.random() < 0.15) {
      delay += 150 + Math.random() * 250
    }

    // ~5% chance of a quick double-tap (like hitting backspace)
    if (Math.random() < 0.05) {
      delay = 30 + Math.random() * 20
    }

    this.timeoutId = setTimeout(() => {
      if (this.isPlaying) {
        this.playClick()
        this.scheduleNext()
      }
    }, delay)
  }

  /**
   * Start the typing sound effect.
   * @param profile - Sound profile: 'typing' for keyboard, 'clicking' for mouse clicks
   */
  start(profile: SoundProfile = 'typing'): void {
    // If already playing, stop first
    if (this.isPlaying) {
      this.stopImmediate()
    }

    this.currentProfile = profile
    const ctx = this.getAudioContext()
    const config = PROFILES[profile]

    // Create master gain node
    this.gainNode = ctx.createGain()
    this.gainNode.gain.value = config.volume
    this.gainNode.connect(ctx.destination)

    this.isPlaying = true

    // Play first click immediately, then schedule the rhythm
    this.playClick()
    this.scheduleNext()

    console.log(`🎹 Typing sound started (profile: ${profile})`)
  }

  /**
   * Stop the typing sound with a smooth fade-out.
   */
  stop(): void {
    if (!this.isPlaying) return

    const config = PROFILES[this.currentProfile]

    // Clear scheduled clicks
    if (this.timeoutId) {
      clearTimeout(this.timeoutId)
      this.timeoutId = null
    }

    // Fade out the master gain
    if (this.gainNode && this.audioCtx) {
      const now = this.audioCtx.currentTime
      this.gainNode.gain.setValueAtTime(this.gainNode.gain.value, now)
      this.gainNode.gain.linearRampToValueAtTime(0, now + config.fadeOutDuration / 1000)

      // Disconnect after fade completes
      setTimeout(() => {
        this.isPlaying = false
        if (this.gainNode) {
          try { this.gainNode.disconnect() } catch (_) { /* already disconnected */ }
          this.gainNode = null
        }
        console.log('🎹 Typing sound stopped')
      }, config.fadeOutDuration + 50)
    } else {
      this.isPlaying = false
    }
  }

  /**
   * Stop immediately without fade (used internally for restart).
   */
  private stopImmediate(): void {
    this.isPlaying = false
    if (this.timeoutId) {
      clearTimeout(this.timeoutId)
      this.timeoutId = null
    }
    if (this.gainNode) {
      try { this.gainNode.disconnect() } catch (_) { /* already disconnected */ }
      this.gainNode = null
    }
  }

  /**
   * Clean up all audio resources. Call when the widget unmounts.
   */
  dispose(): void {
    this.stopImmediate()
    if (this.audioCtx && this.audioCtx.state !== 'closed') {
      // Don't close the AudioContext here — it may be shared with the voice session.
      // Just disconnect our nodes.
      this.audioCtx = null
    }
  }

  /** Check if currently playing */
  get playing(): boolean {
    return this.isPlaying
  }
}

// Export a singleton instance for the widget to use
export const typingSound = new TypingSoundGenerator()
